import { makeAutoObservable, runInAction } from 'mobx'
import { apiClient } from '@/services/api'
import type {
  Agent,
  AgentCreate,
  AgentUpdate,
  PaginatedResponse,
  AgentFramework,
  AgentStatus,
  CentralizedAgent,
  AgentTag,
} from '@/types'
import { AGENT_TAGS, ALL_AGENT_TAGS } from '@/types'

class AgentStore {
  agents: Agent[] = []
  selectedAgent: Agent | null = null
  isLoading = false
  error: string | null = null

  // Pagination
  currentPage = 1
  pageSize = 10
  totalAgents = 0

  // Filters
  searchQuery = ''
  frameworkFilter: AgentFramework | 'all' = 'all'
  statusFilter: AgentStatus | 'all' = 'all'

  // Centralized agents (from /api/v1/agents/centralized)
  centralizedAgents: CentralizedAgent[] = []
  centralizedLoading = false
  centralizedError: string | null = null

  // Tag filtering for centralized agents
  selectedTags: AgentTag[] = []
  availableTags: AgentTag[] = ALL_AGENT_TAGS

  // Tab state
  activeTab: 'custom' | 'system' = 'system'  // 'system' = Internal, 'custom' = External

  constructor() {
    makeAutoObservable(this)
  }

  // Computed values
  get filteredAgents() {
    const agents = this.agents || []
    return agents.filter((agent) => {
      const matchesSearch =
        !this.searchQuery ||
        agent.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        agent.description?.toLowerCase().includes(this.searchQuery.toLowerCase())

      const matchesFramework =
        this.frameworkFilter === 'all' || agent.framework === this.frameworkFilter

      const matchesStatus =
        this.statusFilter === 'all' || agent.status === this.statusFilter

      return matchesSearch && matchesFramework && matchesStatus
    })
  }

  get hasActiveFilters() {
    return (
      this.searchQuery !== '' ||
      this.frameworkFilter !== 'all' ||
      this.statusFilter !== 'all'
    )
  }

  // Centralized agents with tags and filtering
  get centralizedAgentsWithTags(): CentralizedAgent[] {
    return this.centralizedAgents.map((agent) => ({
      ...agent,
      tags: AGENT_TAGS[agent.name] || ['automation'],
    }))
  }

  get filteredCentralizedAgents(): CentralizedAgent[] {
    let agents = this.centralizedAgentsWithTags

    // Apply search filter
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase()
      agents = agents.filter(
        (agent) =>
          agent.name.toLowerCase().includes(query) ||
          agent.display_name.toLowerCase().includes(query) ||
          agent.description?.toLowerCase().includes(query)
      )
    }

    // Apply tag filter
    if (this.selectedTags.length > 0) {
      agents = agents.filter((agent) =>
        agent.tags?.some((tag) => this.selectedTags.includes(tag))
      )
    }

    return agents
  }

  get hasActiveCentralizedFilters() {
    return this.searchQuery !== '' || this.selectedTags.length > 0
  }

  // Actions
  async fetchAgents(workspaceId?: string) {
    this.isLoading = true
    this.error = null

    try {
      const response: PaginatedResponse<Agent> = await apiClient.getAgents({
        workspace_id: workspaceId,
        skip: (this.currentPage - 1) * this.pageSize,
        limit: this.pageSize,
      })

      runInAction(() => {
        this.agents = response.items
        this.totalAgents = response.total
        this.isLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.agents = []
        this.error = error.response?.data?.detail || 'Failed to fetch agents'
        this.isLoading = false
      })
    }
  }

  async fetchAgentById(id: string) {
    this.isLoading = true
    this.error = null

    try {
      const agent = await apiClient.getAgent(id)

      runInAction(() => {
        this.selectedAgent = agent
        this.isLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to fetch agent'
        this.isLoading = false
      })
    }
  }

  async createAgent(data: AgentCreate) {
    this.isLoading = true
    this.error = null

    try {
      const agent = await apiClient.createAgent(data)

      runInAction(() => {
        this.agents = [agent, ...this.agents]
        this.totalAgents += 1
        this.isLoading = false
      })

      return agent
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to create agent'
        this.isLoading = false
      })
      throw error
    }
  }

  async updateAgent(id: string, data: AgentUpdate) {
    this.isLoading = true
    this.error = null

    try {
      const updatedAgent = await apiClient.updateAgent(id, data)

      runInAction(() => {
        this.agents = this.agents.map((agent) =>
          agent.id === id ? updatedAgent : agent
        )
        if (this.selectedAgent?.id === id) {
          this.selectedAgent = updatedAgent
        }
        this.isLoading = false
      })

      return updatedAgent
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to update agent'
        this.isLoading = false
      })
      throw error
    }
  }

  async deleteAgent(id: string) {
    this.isLoading = true
    this.error = null

    try {
      await apiClient.deleteAgent(id)

      runInAction(() => {
        this.agents = this.agents.filter((agent) => agent.id !== id)
        this.totalAgents -= 1
        if (this.selectedAgent?.id === id) {
          this.selectedAgent = null
        }
        this.isLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to delete agent'
        this.isLoading = false
      })
      throw error
    }
  }

  async executeAgent(id: string, inputData: Record<string, any>) {
    this.isLoading = true
    this.error = null

    try {
      const execution = await apiClient.createExecution({
        agent_id: id,
        input: inputData,
      })

      runInAction(() => {
        this.isLoading = false
      })

      return execution
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to execute agent'
        this.isLoading = false
      })
      throw error
    }
  }

  // Centralized agents actions
  async fetchCentralizedAgents(domain?: string) {
    this.centralizedLoading = true
    this.centralizedError = null

    try {
      const agents = await apiClient.getCentralizedAgents({ domain })

      runInAction(() => {
        this.centralizedAgents = agents
        this.centralizedLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.centralizedAgents = []
        this.centralizedError = error.response?.data?.detail || 'Failed to fetch centralized agents'
        this.centralizedLoading = false
      })
    }
  }

  // Tab actions
  setActiveTab(tab: 'custom' | 'system') {
    this.activeTab = tab
  }

  // Tag filter actions
  toggleTag(tag: AgentTag) {
    if (this.selectedTags.includes(tag)) {
      this.selectedTags = this.selectedTags.filter((t) => t !== tag)
    } else {
      this.selectedTags = [...this.selectedTags, tag]
    }
  }

  clearSelectedTags() {
    this.selectedTags = []
  }

  // Filter actions
  setSearchQuery(query: string) {
    this.searchQuery = query
  }

  setFrameworkFilter(framework: AgentFramework | 'all') {
    this.frameworkFilter = framework
  }

  setStatusFilter(status: AgentStatus | 'all') {
    this.statusFilter = status
  }

  clearFilters() {
    this.searchQuery = ''
    this.frameworkFilter = 'all'
    this.statusFilter = 'all'
    this.selectedTags = []
  }

  // Pagination actions
  setPage(page: number) {
    this.currentPage = page
  }

  setPageSize(size: number) {
    this.pageSize = size
    this.currentPage = 1 // Reset to first page
  }

  // Utility actions
  clearError() {
    this.error = null
  }

  clearSelectedAgent() {
    this.selectedAgent = null
  }
}

export const agentStore = new AgentStore()
